import { useEffect, useRef, useState } from 'react';
import { Form } from "../../../system/useForm";
import { Alert, Box, Checkbox, FormControl, FormControlLabel, FormHelperText, Grid, useTheme } from "@mui/material";
import FormYesNo from "../../../Components/FormYesNo";
import FormText from "../../../Components/FormText";
import FormDateTimePicker from "../../../Components/FormDateTimePicker";
import FormChoices from "../../../Components/FormChoices";
import { EngineType } from "../../../system/Domain";
import QuickFAQ from "../CustomerQuickFAQ";
import FormElementLabel from "../../../Components/FormElementLabel";

interface Props {
	form: Form
	isCustomerDelivery: boolean
}

const HasNoValidMainInspectionReason = "Fahrzeuge ohne gültige Hauptuntersuchung/Abgasuntersuchung gelten nicht als fahrbereit.";

const StepCarState = (props: Props) => {
	const theme = useTheme();
	const isElectricCar = !!props.form.getValue('isElectricCar') || props.form.getValue('engineType') === "Electric";

	const [disableDrivable, setDisableDrivable] = useState<boolean>(props.form.getValue('hasValidMainInspection') === false);
	const [displayReasonNotDrivable, setDisplayReasonNotDrivable] = useState<string>(disableDrivable ? HasNoValidMainInspectionReason : "");

	useEffect(() => {
		const hasNoValidMainInspection = props.form.getValue('hasValidMainInspection') === false;

		const cannotBeDrivable = hasNoValidMainInspection;

		if (disableDrivable !== cannotBeDrivable) {
			if (cannotBeDrivable) {
				props.form.setValue('isDrivable', false);
			}

			setDisplayReasonNotDrivable(hasNoValidMainInspection ? HasNoValidMainInspectionReason : "");
			setDisableDrivable(cannotBeDrivable);
		}
	}, [props.form]);

	const engineType: EngineType = props.form.getValue('engineType');

	const engineTypeName = (() => {
		switch (engineType) {
			case "Combustion":
				return "Verbrennungsmotor";
			case "Hybrid":
				return "Hybrid";
			case "Electric":
				return "Elektro";
			case "Unknown":
				return "Unbekannt";
		}
	})();

	const isRegisteredValue = props.form.getValue("isRegistered");
	const isRegistered = isRegisteredValue ?? true;
	const isDrivable = props.form.getValue("isDrivable") ?? true;

	const [zlbiConfirmed, setZlbiConfirmed] = useState<boolean>(() => !!props.form.getValue('zlbiConfirmed'));
	const isFirstRender = useRef(true);

	// Register the zlbiConfirmed field for form validation when isRegistered has been answered
	useEffect(() => {
		if (isRegisteredValue !== undefined && isRegisteredValue !== null) {
			props.form.register('zlbiConfirmed', { required: true });
			return () => {
				props.form.unregister('zlbiConfirmed');
			};
		} else {
			props.form.unregister('zlbiConfirmed');
		}
	}, [isRegisteredValue]);

	// Reset confirmation whenever the isRegistered answer changes (but not on initial mount)
	useEffect(() => {
		if (isFirstRender.current) {
			isFirstRender.current = false;
			return;
		}
		setZlbiConfirmed(false);
		if (isRegisteredValue !== undefined && isRegisteredValue !== null) {
			props.form.setValue('zlbiConfirmed', null);
		}
	}, [isRegisteredValue]);

	return (
		<Box mb={2} mt={2}>
			<Grid container spacing={4}>
				{engineType === "Unknown" && (
					<FormYesNo
						name="isElectricCar"
						label="Ist das Fahrzeug ein Elektroauto? (Hybrid gilt nicht als Elektrofahrzeug)"
						form={props.form}
						options={{ required: true }}
					/>
				)}
				{engineType !== "Unknown" && (
					<Grid item xs={12}>
						Motortyp: {engineTypeName}
					</Grid>
				)}
				<FormYesNo
					name="isRegistered"
					label={
						!props.isCustomerDelivery ?
							<Box>Ist das Fahrzeug zugelassen?
								<QuickFAQ faqKey="unregistered-vehicles" open={!isRegistered} />
							</Box>
							: "Ist das Fahrzeug zugelassen?"
					}
					form={props.form}
					options={{ required: true }}
				/>
				{isRegisteredValue !== undefined && isRegisteredValue !== null && (
					<Grid item xs={12}>
						<Alert severity="warning" sx={{ mb: 1 }}>
							{isRegistered
								? "Die ZLBI (ehemals Fahrzeugschein) sowie beide amtliche Kennzeichen sind zwingend abzugeben."
								: "Die abgemeldete ZLBI (ehemals Fahrzeugschein) ist zwingend abzugeben."}
						</Alert>
						<FormControl error={!!props.form.errors['zlbiConfirmed']}>
							<FormControlLabel
								control={
									<Checkbox
										checked={zlbiConfirmed}
										onChange={(e) => {
											const checked = e.target.checked;
											setZlbiConfirmed(checked);
											props.form.setValue('zlbiConfirmed', checked || null, true);
										}}
									/>
								}
								label="Ich habe die obige Information zur Kenntnis genommen."
							/>
							{!!props.form.errors['zlbiConfirmed'] && (
								<FormHelperText>Bitte bestätigen Sie, dass Sie die Information gelesen haben.</FormHelperText>
							)}
						</FormControl>
					</Grid>
				)}
				<FormYesNo
					name="hasValidMainInspection"
					label={
						<Box>
							Ist die Hauptuntersuchung/Abgasuntersuchung gültig?
							<QuickFAQ faqKey="bring-huau" open={true} />
						</Box>
					}
					form={props.form}
					options={{ required: true }}
				/>
				{props.form.getValue("hasValidMainInspection") === true && (
					<Grid item xs={12}>
						<FormDateTimePicker
							name="mainInspectionValidUntil"
							form={props.form}
							label="HU/AU gültig bis"
							openTo="year"
							views={["year", "month"]}
							variant="date"
							format="MM.YYYY"
							disablePast
							required
						/>
					</Grid>
				)}
				<FormYesNo
					name="isDrivable"
					label={
						<Box mb={0.25}>
							<FormElementLabel text="Ist das Fahrzeug fahrbereit?" form={props.form} name="isDrivable" />
							{!props.isCustomerDelivery && (
								<QuickFAQ faqKey="motor-type-electro" open={isElectricCar && !isDrivable} />
							)}
							<Box color={theme.palette.secondary.light}>{displayReasonNotDrivable}</Box>
						</Box>
					}
					form={props.form}
					disabled={disableDrivable}
					options={{ required: true }}
				/>
				{props.form.getValue("isDrivable") === false && (
					<FormText
						name="drivableComment"
						form={props.form}
						label="Bemerkung zur Fahrbereitschaft"
						type="text"
					/>
				)}
				<FormChoices
					name="tireType"
					label={
						<Box mb={0.25}>
							<FormElementLabel
								text="Welche Reifenart ist aufgezogen?"
								form={props.form}
								name="tireType"
							/>
							<QuickFAQ faqKey="tires" />
						</Box>
					}
					form={props.form}
					choices={{
						"wr": "Winterreifen",
						"sr": "Sommerreifen",
						"gjr": "Ganzjahresreifen",
						"unk": "Unbekannt"
					}}
					options={{ required: true }}
				/>
				<FormChoices
					name="carType"
					label="Ist das Auto ein Kastenwagen oder SUV?"
					form={props.form}
					choices={{
						"SUV": "SUV",
						"Van": "Kastenwagen",
						"Unknown": "Unbekannt",
						"None": "Nein"
					}}
					options={{ required: true }}
				/>
			</Grid>
		</Box>
	)
};

export default StepCarState;
